import React, { ChangeEvent, useState } from 'react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Page } from "@/types/Page"
import { useRouter } from "next/router"
import { WithDefaultLayout } from '@/components/DefautLayout';
import { Title } from '@/components/Title';
import { SectionContainerLight } from '@/components/common/sections/SectionContainerLight';
import { HeaderSection } from '@/components/common/headers/HeaderSection';
import { SelectOptions } from '@/interfaces/RegistrationForm';
import { useController, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ZodType, z } from 'zod';
import { ColorPalette, courseClassList, freeCourseList, freeMobileApplicationScheduleList, freeVideoGameScheduleList, freeFrontEndWebsiteScheduleList, freeBackEndWebsiteScheduleList, listCurrentStatus, listReasonToJoinProgram, workshopCourseList, workshopMobileApplicationScheduleList, workshopVideoGameScheduleList, workshopFrontEndWebsiteScheduleList, workshopBackEndWebsiteScheduleList, bootcampCourseList, bootcampFullStackEngineerScheduleList } from '@/constants';
import { InputRegistrationForm } from '@/components/common/input/InputRegistrationForm';
import { InputSelectRegistrationForm } from '@/components/common/input/InputSelectRegistrationForm';
import { InputCheckBoxGroup } from '@/components/common/input/InputCheckBox';
import { informationSourceList } from '@/constants';
import { useFetchWithAccessToken } from '@/functions/useFetchWithAccessToken';
import { BackendApiUrl } from '@/functions/BackendApiUrl';
import { LoadingBackdrop } from '@/components/common/loading/LoadingBackdrop';
import Popup from '@/components/registration-form/PopupSuccess';
import { Modal } from 'antd';
import { v4 } from "uuid";
import { OnlineRegistrationFormProps } from '@/interfaces/OnlineRegistrationForm';
import axios from 'axios';
import { GetBlobPresignedUrl } from '@/interfaces/GetBlobPresignedUrl';
import OnlineCourseRegistrationForm from '@/interfaces/OnlineCourseRegistrationForm';
import { LoadingOverlay } from '@/components/LoadingOverlay';
import { AppSettings } from '../../functions/AppSettings';
import Script from 'next/script'
import { formatDate } from '@/functions/formatDate';

// Declare global variables for using Google reCaptcha interfaces
declare global {
  interface Window { grecaptcha; }
  interface Grecaptcha { ready; }
  interface Grecaptcha { execute; }
  interface Execute { then; }
}

interface OnlineRegistrationFormPostResponse {
  response: string
}

const schema: ZodType<OnlineRegistrationFormProps> = z.object({
  fullName: z.string({ required_error: 'Nama lengkap tidak boleh kosong' }).min(1, 'Nama lengkap tidak boleh kosong'),
  phoneNumber: z.string({ required_error: 'Nomor HP atau Whatsapp tidak boleh kosong' }).min(8, 'Nomor HP atau Whatsapp tidak boleh kosong').max(16),
  email: z.string({ required_error: 'Email tidak boleh kosong' }).email({ message: 'Format email tidak valid' }).min(1, 'Email tidak boleh kosong'),
  birthday: z.date({ required_error: 'Tanggal lahir tidak boleh kosong' }),
  currentStatus: z.string({ required_error: 'Status saat ini tidak boleh kosong' }).min(1, 'Status saat ini tidak boleh kosong'),
  programClassId: z.number({ required_error: 'Program tidak boleh kosong' }).min(1, 'Program saat ini tidak boleh kosong'),
  programTypeId: z.number({ required_error: 'Topik tidak boleh kosong' }).min(1, 'Topik saat ini tidak boleh kosong'),
  programScheduleId: z.number({ required_error: 'Jadwal tidak boleh kosong' }).min(1, 'Jadwal saat ini tidak boleh kosong'),
  reason: z.string({ required_error: 'Alasan mengikuti program tidak boleh kosong' }).min(1, 'Alasan mengikuti program tidak boleh kosong'),
  informationSource: z.string({ required_error: 'Silahkan pilih salah satu atau lebih', invalid_type_error: 'Silahkan pilih salah satu atau lebih' }).array().nonempty({ message: 'Silahkan pilih salah satu atau lebih' }),
});

const RegistrationForm: Page<{
  reCaptchaSiteKey: string
}> = ({ reCaptchaSiteKey }) => {
  const { replace } = useRouter();
  const { fetchPOST, fetchGET } = useFetchWithAccessToken();
  const [isLoadingPost, setIsLoadingPost] = useState(false);
  const [showPopupSuccess, setShowPopupSuccess] = useState(false);
  const { watch, setValue, register, handleSubmit, control, reset, formState: { errors, } } = useForm<OnlineRegistrationFormProps>({
    resolver: zodResolver(schema)
  });
  const selectedDate = watch('birthday')
  const { field: fieldCurrentStatus } = useController({ name: 'currentStatus', control });
  const { field: fieldReasonToJoin } = useController({ name: 'reason', control });
  const { field: fieldProgramClass } = useController({ name: 'programClassId', control });
  const { field: fieldProgramType } = useController({ name: 'programTypeId', control });
  const { field: fieldProgramSchedule } = useController({ name: 'programScheduleId', control });
  const [paymentBlobId, setPaymentBlobId] = useState<string | undefined>();
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const onSubmit = async (formData: OnlineRegistrationFormProps) => {
    const date = formatDate(selectedDate)
    setIsLoadingPost(true);
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(reCaptchaSiteKey, { action: "homepage" })
        .then(async (token) => {
          const payload: OnlineCourseRegistrationForm = {
            fullName: formData.fullName,
            phoneNumber: formData.phoneNumber,
            email: formData.email,
            birthday: date,
            currentStatus: formData.currentStatus,
            programTypeId: formData.programTypeId,
            programScheduleId: formData.programScheduleId,
            reason: formData.reason,
            informationSource: formData.informationSource,
            reCaptchaToken: token
          };

          if (fieldProgramClass.value === 2) {
            payload.paymentBlobId = paymentBlobId;
          }

          const { data, error, problem } = await fetchPOST<OnlineRegistrationFormPostResponse>(BackendApiUrl.onlineCourseRegistration, payload);
          setIsLoadingPost(false);
          if (problem || error) {
            showError()
          }
          if (data?.response) {
            setShowPopupSuccess(true);
            reset();
          }
        })
    })


  };

  const showError = () => {
    Modal.error({
      title: 'Something Went Wrong',
      content: 'Please contact with our support email marketing.department@accelist.com',
      okText: 'Ok',
      okButtonProps: {
        style: {
          backgroundColor: ColorPalette.alertPrimary
        }
      }
    });
  };

  const renderLoading = () => {
    if (isLoadingPost) {
      return <LoadingBackdrop />
    }
    return null;
  };

  const renderPopupSuccess = () => {
    if (showPopupSuccess) {
      return <Popup onGoToHome={() => {
        setShowPopupSuccess(false)
        replace({
          pathname: '/'
        })
      }} />
    }
    return null
  }


  async function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;

    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];
    const fileName = file?.name;
    const fileType = file?.type;
    const fileId = v4();
    setPaymentBlobId(fileId);

    // Fetch the presigned URL for uploading
    const response = await fetchGET<GetBlobPresignedUrl>(`/api/be/api/v1/payment-blob/presigned-put-object?fileName=${fileId}`);
    const presignedUrl = response.data?.fileUrl;

    if (!presignedUrl) {
      return;
    }

    try {
      setIsUploading(true);
      // Upload the file using the presigned URL
      await axios.put(presignedUrl, file, {
        headers: {
          'Content-Type': fileType
        }
      });

      await fetchPOST(`/api/be/api/v1/payment-blob/blob-information?id=${fileId}&fileName=${fileName}&mime=${fileType}`);
      setIsUploading(false);
    } catch (error) {
      setIsUploading(false);
      console.error(error);
      showError();
    }
  }

  // Mapping program class to course lists
  const courseListMap = {
    1: freeCourseList,
    2: workshopCourseList,
    3: bootcampCourseList
  };

  // Mapping program and topic to schedule list
  const scheduleMap = {
    1: {
      1: freeVideoGameScheduleList,
      2: freeFrontEndWebsiteScheduleList,
      3: freeBackEndWebsiteScheduleList,
      4: freeMobileApplicationScheduleList,
    },
    2: {
      5: workshopVideoGameScheduleList,
      6: workshopFrontEndWebsiteScheduleList,
      7: workshopBackEndWebsiteScheduleList,
      8: workshopMobileApplicationScheduleList,
    },
    3:{
      9: bootcampFullStackEngineerScheduleList
    }
  };

  return (
    <div>
      <LoadingOverlay isActive={isUploading} loadingText='Mengunggah berkas...' />
      <SectionContainerLight id='section-form'>
        <HeaderSection title='Formulir Pendaftaran' subtitle='Silakan isi form di bawah ini untuk mendaftarkan diri kamu' />
        <div className="flex flex-col px-6 md:px-12 lg:px-36 mt-10 md:mt-16">
          <form onSubmit={handleSubmit(onSubmit)}>
            <InputRegistrationForm
              id={'fullName'}
              label='Nama Lengkap'
              register={register('fullName', { required: true })}
              placeholder='Nama'
              formErrorMessage={errors.fullName?.message}
            />
            <InputRegistrationForm
              id={'phoneNumber'}
              label='Nomor HP atau Whatsapp'
              register={register('phoneNumber', { required: true })}
              placeholder='Isi nomor WhatsApp kamu'
              formErrorMessage={errors?.phoneNumber?.message}
            />
            <InputRegistrationForm
              id={'email'}
              label='Email'
              register={register('email', { required: true })}
              placeholder='Email@gmail.com'
              formErrorMessage={errors?.email?.message}
            />
            <div className="mb-5 md:mb-8 w-full">
              <label className="text-lg text-secondary-100 font-body font-bold mb-2">Tanggal Lahir</label>
              <div className="customDatePickerWidth">
                <DatePicker className={`w-full form-control p-4 border-2 ${errors?.birthday ? 'border-red-600' : 'border-black'} rounded-md mt-2`}
                  selected={selectedDate}
                  onChange={(date) => setValue("birthday", date)}
                  dateFormat="dd-MM-yyyy"
                  placeholderText="Pilih Tanggal Lahir"
                  peekNextMonth
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                />
                {errors?.birthday && (
                  <p className="text-red-600 text-sm border-alertdanger mt-1">
                    {errors.birthday.message}
                  </p>
                )}
              </div>
            </div>
            <InputSelectRegistrationForm
              label='Status Saat Ini'
              value={listCurrentStatus.find(e => e.value === fieldCurrentStatus.value) ?? ''}
              name={fieldCurrentStatus.name}
              options={listCurrentStatus}
              onChange={(selectedOptions: SelectOptions<string>) => fieldCurrentStatus.onChange(selectedOptions.value)}
              placeholder='Pilih Status Kamu'
              formErrorMessage={errors?.currentStatus?.message}
            />
            <InputSelectRegistrationForm
              label='Pilih Program'
              value={courseClassList.find(e => e.value === fieldProgramClass.value) ?? ''}
              name={fieldProgramClass.name}
              options={courseClassList}
              onChange={(selectedOptions: SelectOptions<string>) => fieldProgramClass.onChange(selectedOptions.value)}
              placeholder='Pilih Program yang Kamu ingin ikuti'
              formErrorMessage={errors?.programClassId?.message}
            />
            {fieldProgramClass.value && (
              <InputSelectRegistrationForm
                label='Pilih Topik'
                value={courseListMap[fieldProgramClass.value].find(e => e.value === fieldProgramType.value) ?? ''}
                name={fieldProgramType.name}
                options={courseListMap[fieldProgramClass.value]}
                onChange={(selectedOptions: SelectOptions<string>) => fieldProgramType.onChange(selectedOptions.value)}
                placeholder="Pilih Topik yang Kamu ingin ikuti"
                formErrorMessage={errors?.programTypeId?.message}
              />
            )}
            {fieldProgramClass.value && fieldProgramType.value && (
              <InputSelectRegistrationForm
                label='Pilih Jadwal'
                value={scheduleMap[fieldProgramClass.value][fieldProgramType.value].find(e => e.value === fieldProgramSchedule.value) ?? ''}
                name={fieldProgramSchedule.name}
                options={scheduleMap[fieldProgramClass.value][fieldProgramType.value]}
                onChange={(selectedOptions: SelectOptions<string>) => fieldProgramSchedule.onChange(selectedOptions.value)}
                placeholder="Pilih Jadwal yang Kamu ingin ikuti"
                formErrorMessage={errors?.programScheduleId?.message}
              />
            )}
            <InputSelectRegistrationForm
              label='Alasan Mengikuti Program Ini'
              value={listReasonToJoinProgram.find(e => e.value === fieldReasonToJoin.value) ?? ''}
              name={fieldReasonToJoin.name}
              options={listReasonToJoinProgram}
              onChange={(selectedOptions: SelectOptions<string>) => fieldReasonToJoin.onChange(selectedOptions.value)}
              placeholder='Pilih Alasan Kamu'
              formErrorMessage={errors?.reason?.message}
            />
            { (fieldProgramClass.value === 2 || fieldProgramClass.value === 3) && (
              <>
                <div className='mb-5 md:mb-8'>
                  <div className='mb-2 md:mb-2'>
                    <label className='text-lg text-secondary-100 font-body font-bold'>
                      Jenis Pembayaran Program
                    </label>
                  </div>
                  <div className='mb-5 p-4 md:mb-8 border-2 border-black rounded-md'>
                    Berbayar ke <strong>BCA 8833445888 PT Accelist Edukasi Indonesia</strong>
                  </div>
                </div>
                <div className='mb-5 md:mb-8'>
                  <label className='text-lg text-secondary-100 font-body font-bold mb-5'>
                    Bukti Pembayaran WorkShop
                  </label>
                  <div className='my-3 flex'>
                    <input type='file' onChange={(e) => handleChange(e)} id='paymentBlobId'></input>
                    <div className='mx-3'>
                      <p className='text-gray-400'>Upload image bukti pembayaran kamu</p>
                      <p className='text-gray-600 font-semibold'>Pembayaran dilakukan ke BCA 8833445888 PT Accelist Edukasi Indonesia</p>
                    </div>
                  </div>
                </div>
              </>
            )}

            <InputCheckBoxGroup
              name='informationSource'
              label='Darimana Kamu Mengetahui Info Tentang Accelist Edukasi Indonesia?'
              options={informationSourceList}
              register={register('informationSource')}
              formErrorMessage={errors.informationSource?.message}
            />
            <button type="submit" className='text-lg text-white font-body font-bold bg-accent-100 cursor-pointer py-3.5 px-11 rounded hover:bg-secondary-100 transition duration-300 ease-out'
              disabled={isLoadingPost}>
              Kirim Pendaftaran
            </button>
          </form>
        </div>
        {renderLoading()}
        {renderPopupSuccess()}
      </SectionContainerLight>
    </div>
  );
};

const RegistrationFormPage: Page<{
  reCaptchaSiteKey: string
}> = ({ reCaptchaSiteKey }) => {
  return (
    <div>
      <Title title={'Pengisian formulir'} description='Masukan data diri kamu untuk mendaftar program pilihanmu.' />
      <Script src={`https://www.google.com/recaptcha/api.js?render=${reCaptchaSiteKey}`} />
      <RegistrationForm reCaptchaSiteKey={reCaptchaSiteKey}></RegistrationForm>
    </div>
  );
};

RegistrationFormPage.layout = WithDefaultLayout;

export async function getServerSideProps() {
  return {
    props: {
      reCaptchaSiteKey: AppSettings.current.reCaptchaSiteKey,
    }
  }
}

export default RegistrationFormPage;